import React, { useState, useRef, forwardRef } from 'react'
import { AnimatedTextInputProps } from './types'
import { useTheme } from '@/hooks/useTheme'
import { BaseText, BaseView } from '../base'
import { SecureTextEntry } from './subComponents/SecureTextEntry'

// Константы размеров (как в React Native версии)
// Используем фиксированные значения вместо responsiveAdapterVertical чтобы избежать изменения размеров при появлении клавиатуры
const inputHeight = 70
const textAreaHeight = 50
const maxInputHeight = 34
const initialFontSize = 16

// Простые веб-компоненты
const Spinner: React.FC<{ size?: number; color?: string }> = ({
  size = 20,
  color = '#3B82F6',
}) => (
  <div
    className="animate-spin rounded-full border-2 border-transparent"
    style={{
      width: `${size}px`,
      height: `${size}px`,
      borderTopColor: color,
      borderRightColor: color,
    }}
  />
)

const CheckIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 20,
  color = '#10B981',
}) => (
  <div
    className="flex items-center justify-center"
    style={{
      width: `${size}px`,
      height: `${size}px`,
      color,
    }}
  >
    ✓
  </div>
)

const CloseIcon: React.FC<{
  size?: number
  color?: string
  onClick?: () => void
}> = ({ size = 20, color = '#6B7280', onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="hover:bg-gray-100 rounded p-1 transition-colors"
    style={{ color }}
  >
    <div
      className="flex items-center justify-center"
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      ✕
    </div>
  </button>
)

export const AnimatedTextInput = forwardRef<
  HTMLInputElement,
  AnimatedTextInputProps
>(
  (
    {
      label,
      withoutBorder = true,
      errorMessage,
      afterOnBlur,
      success = false,
      isTextArea = false,
      showCount = false,
      contentContainerStyle,
      onClear,
      isHiddenErrorMessage = false,
      _isHiddenBorderError = false,
      iconRight,
      isLoading,
      placeholder,
      onChangeText,
      secureTextEntry = false,
      keyboardType = 'default',
      textContentType,
      value,
      onChange,
      onFocus,
      onBlur,
      maxLength,
      autoComplete,
      className,
      style,
      onPress,
      _autoCapitalize,
      editable = true,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme()
    const [focused, setFocused] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    // Используем переданный ref или наш внутренний
    const actualRef = ref || inputRef

    // Создаем чистые props для input/textarea
    const {
      inline: _inline,
      inputFocusedParam: _inputFocusedParam,
      showErrorState: _showErrorState,
      returnKeyType: _returnKeyType,
      importantForAutofill: _importantForAutofill,
      isHiddenBorderError: _isHiddenBorderError2,
      autoCapitalize: _autoCapitalize2,
      ...cleanRest
    } = rest

    // Определяем режим кнопки
    const isButtonMode = !!onPress && editable === false

    const handleFocus = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setFocused(true)
      onFocus?.(e as React.FocusEvent<HTMLInputElement>)
    }

    const handleContainerPress = (e: React.MouseEvent) => {
      if (onPress) {
        onPress(e)
        return
      }
      // Если не режим кнопки, фокусируем инпут
      if (
        !isButtonMode &&
        actualRef &&
        'current' in actualRef &&
        actualRef.current
      ) {
        actualRef.current.focus()
      }
    }

    const handleBlur = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setFocused(false)
      onBlur?.(e as React.FocusEvent<HTMLInputElement>)
      afterOnBlur?.()
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      onChangeText?.(e.target.value)
    }

    const handleClear = () => {
      if (actualRef && 'current' in actualRef && actualRef.current) {
        actualRef.current.value = ''
        // Trigger change event
        const event = new Event('input', { bubbles: true })
        actualRef.current.dispatchEvent(event)
      }
      onClear?.()
    }

    // Определяем тип инпута
    const getInputType = (): string => {
      if (secureTextEntry && !showPassword) return 'password'
      if (keyboardType === 'email-address') return 'email'
      if (keyboardType === 'numeric') return 'number'
      if (keyboardType === 'phone-pad') return 'tel'
      return 'text'
    }

    // Определяем автокомплит
    const getAutoComplete = (): string => {
      if (autoComplete) return autoComplete
      if (textContentType === 'password') return 'current-password'
      if (textContentType === 'newPassword') return 'new-password'
      if (textContentType === 'emailAddress') return 'email'
      if (textContentType === 'telephoneNumber') return 'tel'
      return 'off'
    }

    // Определяем есть ли ошибка
    const thereIsError = errorMessage

    // Определяем границу (как в React Native версии)
    const handleRenderBorder = () => {
      if (thereIsError) return 1.5
      else if (withoutBorder) return 0
      else if (focused) return 1.5
      return 0
    }

    // Стили контейнера
    const containerStyles: React.CSSProperties = {
      gap: 2,
      ...contentContainerStyle,
    }

    const inputStyles: React.CSSProperties = {
      padding: '38px 12px 8px 12px',
      borderColor:
        errorMessage && !_isHiddenBorderError
          ? theme.colors.label.textInputError
          : focused
            ? theme.colors.label.primary
            : theme.colors.background.secondaryGray,
      height: isTextArea ? inputHeight * 1.5 : inputHeight,
      backgroundColor: theme.colors.background.textInput,
      borderWidth: handleRenderBorder(),
      borderRadius: 16,
      paddingLeft: 12,
      paddingRight: 12,
      paddingTop: 8,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 4,
      fontSize: initialFontSize,
      color: theme.colors.label.textTextInput,
      outline: 'none',
      ...style,
    }

    return (
      <div
        style={containerStyles}
        className={`relative w-full ${className || ''}`}
      >
        {/* Контейнер инпута (как BasePressable в RN) */}
        <div
          style={{
            ...inputStyles,
            cursor: isButtonMode ? 'pointer' : 'text',
          }}
          className="relative flex items-center"
          onClick={handleContainerPress}
        >
          <BaseText
            fontFamily="medium"
            fontSize={16}
            style={{
              position: 'absolute',
              left: 12,
              top: 10,
              color: theme.colors.label.labelTextInput,
            }}
          >
            {label}
          </BaseText>

          {isTextArea ? (
            <textarea
              ref={actualRef as any}
              style={{
                fontSize: initialFontSize,
                color: theme.colors.label.textTextInput,
                flex: 1,
                flexShrink: 0,
                height: textAreaHeight,
                backgroundColor: 'transparent',
                border: 'none',
                fontWeight: '500',
                outline: 'none',
                resize: 'none',
                paddingTop: 8,
              }}
              placeholder={focused ? placeholder : ''}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange as any}
              maxLength={maxLength}
              value={value}
              readOnly={isButtonMode}
            />
          ) : (
            <input
              ref={actualRef}
              type={getInputType()}
              style={{
                fontSize: initialFontSize,
                color: theme.colors.label.textTextInput,
                flex: 1,
                flexShrink: 0,
                height: 'auto',
                minHeight: maxInputHeight,

                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                paddingTop: 20,
                pointerEvents: isButtonMode ? 'none' : 'auto',
                fontWeight: '500',
              }}
              placeholder={focused ? placeholder : ''}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              maxLength={maxLength}
              autoComplete={getAutoComplete()}
              value={value}
              readOnly={isButtonMode}
              {...cleanRest}
            />
          )}

          {/* Иконки справа */}
          {success && (
            <CheckIcon size={25} color={theme.colors.icon?.primary} />
          )}

          <SecureTextEntry
            secureTextEntry={secureTextEntry}
            success={success}
            onPress={setShowPassword}
          />

          {iconRight && !isLoading && iconRight}

          {isLoading && <Spinner color={theme.colors.icon?.primary} />}

          {onClear && value && !secureTextEntry && (
            <CloseIcon
              onClick={handleClear}
              size={20}
              color={theme.colors.label.description}
            />
          )}
        </div>

        {/* Сообщение об ошибке */}
        {errorMessage && !isHiddenErrorMessage && (
          <div
            style={{
              color: theme.colors.label.textInputError,
              fontSize: 12,
              marginLeft: 6,
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* Счетчик символов */}
        {showCount && maxLength && (
          <BaseView
            style={{
              color: theme.colors.label.description,
              fontSize: 12,
              textAlign: 'right',
              paddingTop: 6,
              paddingRight: 12,
            }}
          >
            <BaseText
              fontFamily="regular"
              fontSize={16}
              color={theme.colors.label.description}
            >
              {value?.toString().length || 0}/{maxLength}
            </BaseText>
          </BaseView>
        )}
      </div>
    )
  },
)

AnimatedTextInput.displayName = 'AnimatedTextInput'
